import { useQuery } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { Spinner } from "~/components/ui/spinner";
import { QUERY_KEYS } from "~/constants/queryKeys";
import { apiClient } from "~/lib/orpc-client";
import Application from "~/components/application/wizard/application-wizard";

export const Route = createFileRoute("/_protected/apply/$applicationId")({
  component: RouteComponent,
});

function RouteComponent() {
  const { applicationId } = Route.useParams();
  const navigate = useNavigate();
  const isNew = applicationId === "new";

  const {
    data: application,
    isLoading: isLoadingApplication,
    error,
    isError,
    fetchStatus,
  } = useQuery({
    queryKey: QUERY_KEYS.APPLICATION_BY_ID(applicationId ?? ""),
    queryFn: () =>
      apiClient.applications.getById({
        id: Number(applicationId),
      }),
    enabled: !!applicationId && !isNew,
    retry: false,
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 5 * 60 * 1000, // Keep in cache for 5 minutes
  });

  // Check ownership - redirect non-owners to view page (but not for in-progress apps)
  const isOwner = application?.isOwner !== false;
  const isInProgress = application?.applicationStatus === "IN_PROGRESS";

  useEffect(() => {
    // If user doesn't own this application and it's not in progress, redirect to view-only page
    if (application && !isOwner && !isInProgress && !isNew) {
      navigate({
        to: `/applications/${applicationId}`,
        replace: true,
      });
    }
  }, [application, isOwner, isInProgress, isNew, applicationId, navigate]);

  // Don't show loading if we have cached data
  if (isLoadingApplication && !application && !isNew) {
    return (
      <div className="h-screen w-screen flex justify-center items-center">
        <Spinner className="size-12" />
      </div>
    );
  }

  // Only show error if we're not still fetching and have no data
  if (isError && !isNew && !application && fetchStatus === "idle") {
    console.error("Error loading application:", error);
    return (
      <div className="h-screen w-screen flex justify-center items-center">
        <div className="text-center">
          <p className="text-destructive font-medium mb-2">
            Error loading application
          </p>
          <p className="text-sm ">
            {error?.message || "Unable to load application"}
          </p>
        </div>
      </div>
    );
  }

  // Don't render edit form for non-owned, non-in-progress applications (will redirect)
  if (!isOwner && !isInProgress && !isNew) {
    return (
      <div className="h-screen w-screen flex justify-center items-center">
        <Spinner className="size-12" />
      </div>
    );
  }
  // Default to v1 application process
  return <Application application={application} isNew={isNew} />;
}
